import React, { Fragment, useState } from "react";
import { Card, CardBody, CardHeader, Col, Row, Table, Modal, ModalHeader, ModalBody, ModalFooter, Collapse, Progress } from 'reactstrap';
import dateFormat from "dateformat";

import { createDeepClone } from "../utils/index";

import ConfirmModal from "./ConfirmModal";
import ErrorModal from "./ErrorModal";
import PhotoModal from "./PhotoModal";
import PdfModal from "./PdfModal";
import WordModal from "./WordModal";
import CardLoader from "./CardLoader";
import MyDateRenderer from "./MyDateRenderer";
import { buildDownloadUrl, getFileTitle, getFileExtension } from "../utils/index";

import { useDispatch, useSelector } from "react-redux";
import * as documentActions from "../actions/DocumentActions";
import * as commonActions from "../actions/CommonActions";

const IdiServiceDocumentGrid = (props) => {
    const dispatch = useDispatch();

    const [showModal, setShowModal] = useState(false);
    const [showMultipleFileMessageModal, setShowMultipleFileMessageModal] = useState(false);
    const [multipleFileMessage, setMultipleFileMessage] = useState("");
    const [allowMultiple, setAllowMultiple] = useState(true);
    const [defaultFileType, setDefaultFileType] = useState("*");
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [currentUploadProgress, setCurrentUploadProgress] = useState(false);
        
    const [deleteId, setDeleteId] = React.useState(0);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);

    const [selectedItem, setSelectedItem] = useState(null);
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [showWordModal, setShowWordModal] = useState(false);

    const downloading = useSelector((state) => state.document.downloading);
    const documentTypeList = useSelector((state) => state.login.data.documentTypeList);

    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [fboState, setFBOState] = React.useState(null);
    const [documentList, setDocumentList] = React.useState([]);
    const [gridCollapsed, setGridCollapsed] = React.useState(props.collapsed === "false" ? false : true);

    React.useEffect(() => {
        async function fetchData() {
            setLoading(true);
        
            var data = await documentActions.loadServiceDocument(props.serviceId, props.refId, props.refName, props.parentId, props.parentName, props.isProduct, (props.skipAudit || false));
            if(data.error) {
                dispatch(commonActions.setErrorMessage(data.error));
            }
            else {
                setFBOState(data.fbo);
                setDocumentList(data.list);
            }
    
            setLoading(false);
        }

        fetchData();
    }, [props.serviceId, props.refId, props.refName, props.parentId, props.parentName, props.isProduct, props.skipAudit, props.refreshAction]);

    React.useEffect(() => {
        if(multipleFileMessage && multipleFileMessage.length > 0) {
            setShowMultipleFileMessageModal(true);
        }
    }, [multipleFileMessage]);

    const onDocumentChange = (name, value) => {
        var copy_fboState = createDeepClone(fboState);

        if(value) {
            copy_fboState[name] = value;
        }
        else if(value === false) {
            copy_fboState[name] = false;
        }
        else {
            copy_fboState[name] = null;
        }
        
        setFBOState(copy_fboState);
    }

    const onFileChange = (event) => {
        if(event.target.files.length > 0) {
            var files = [...event.target.files];
            var copy_selectedFiles = createDeepClone(selectedFiles);
            
            files.map(function(item, index) {
                copy_selectedFiles.push({
                    file: item, 
                    name: item.name, 
                    size: item.size, 
                    title: getFileTitle(item), 
                    titleValid: true,
                    uploaded: false,
                    progress: 0, 
                    error: ""
                });
            });

            setSelectedFiles(copy_selectedFiles);
        }
    }

    const onFileTitleChange = (index, value) => {
        var copy_selectedFiles = createDeepClone(selectedFiles);
        if(value) {
            copy_selectedFiles[index].title = value;
            copy_selectedFiles[index].titleValid = true;
        }
        else {
            copy_selectedFiles[index].title = null;
            copy_selectedFiles[index].titleValid = false;
        }
        
        setSelectedFiles(copy_selectedFiles);
    }

    const onFileDelete = (index) => {
        var copy_selectedFiles = createDeepClone(selectedFiles);
        copy_selectedFiles.splice(index, 1);
        setSelectedFiles(copy_selectedFiles);
    }

    const showModalDialog = async (item) => {
        onDocumentChange("documentType", item.code);
        
        setSelectedFiles([]);
        setAllowMultiple(item.allowMultiple);
        setMultipleFileMessage("");

        if(item.code === "CUSTOMER_LOGO") {
            setDefaultFileType(".bmp,.dib,.rle,.jpg,.jpeg,.jpe,.jfif,.gif,.emf,.wmf,.tif,.tiff,.png,.ico");
        }
        else {
            setDefaultFileType("*");
        }
                
        if(item.allowMultiple) {
            setShowModal(true);
        }
        else{
            setLoading(true);
            var data = await documentActions.loadServiceDocument(props.serviceId, props.refId, props.refName, props.parentId, props.parentName, props.isProduct, (props.skipAudit || false));
            setLoading(false);

            if(data.error) {
                dispatch(commonActions.setErrorMessage(data.error));
            }
            else {
                var canUpload = true;
                var documentName = "document";

                data.list.forEach(document => {
                    if(document.code === item.code && document.documentList && document.documentList.length > 0) {
                        canUpload = false;
                        documentName = document.name;
                    }
                });

                if(canUpload) {
                    setShowModal(true);
                }
                else {
                    setMultipleFileMessage(documentName);
                }
            }
        }
    }

    const hideModalDialog = () => {
        setShowModal(false);
    }

    const uploadFile = async (file, index) => {
        setSaving(true);
        setCurrentUploadProgress(0);
        
        var uploadSuccess = true;
        var uploadError = "";
        var tempFileName = "";
        var chunkSize = 1048576 * 5;
        var chunkCounter = 0;
        var chunkTotal = file.size % chunkSize == 0 ? file.size / chunkSize : Math.floor(file.size / chunkSize) + 1;
        var chunkStart = 0;
        var chunkEnd = chunkSize;

        selectedFiles[index].uploaded = false;
        selectedFiles[index].progress = 0;
        selectedFiles[index].error = "";

        while (chunkCounter <= chunkTotal && uploadSuccess === true) {
            var chunk = file.file.slice(chunkStart, chunkEnd);
            
            const uploadFormData = new FormData();
            uploadFormData.append("tempFileName", tempFileName);
            uploadFormData.append("file", chunk, file.name);

            var data = await documentActions.uploadFileChunk(uploadFormData);
            if(data.success) {
                tempFileName = data.tempFileName;
                uploadSuccess = true;
                uploadError = "";

                var percentage = Math.round((chunkCounter / chunkTotal) * 100);
                selectedFiles[index].progress = percentage;

                setCurrentUploadProgress(percentage);
            }
            else {
                uploadSuccess = false;
                uploadError = data.error;
            }

            chunkCounter += 1;
            chunkStart = chunkEnd;
            chunkEnd += chunkSize;
        }

        if(uploadSuccess) {
            const formData = new FormData();
                            
            formData.append("serviceId", props.serviceId);
            formData.append("isProduct", props.isProduct);
            formData.append("document", JSON.stringify(fboState));
            formData.append("title", file.title ? file.title : file.name);
            formData.append("originalFileName", file.name);
            formData.append("tempFileName", tempFileName);

            var data = await documentActions.saveServiceDocument(formData);
            if(data.error) {
                uploadSuccess = false;
                uploadError = data.error;
            }
            else {
                uploadSuccess = true;
                uploadError = "";

                setDocumentList(data.list);

                if(props.onChange) {
                    props.onChange(props.isProduct);
                }
            }
        }

        selectedFiles[index].uploaded = uploadSuccess;
        selectedFiles[index].error = uploadError;
        
        setSelectedFiles(selectedFiles);
        setSaving(false);
    }

    const onSaveDocumentClick = async () => {
        if(selectedFiles && selectedFiles.length > 0) {
            var valid = true;
            
            selectedFiles.map(function(item, index){
                if(!item.titleValid) {
                    valid = false;
                }
            });

            if(valid) {
                await SaveDocument();
            }
        }
        else {
            dispatch(commonActions.setErrorMessage("No file is selected."));
        }
    }

    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    const SaveDocument = async () => {
        var allFilesUploaded = true;
        var closeDialog = true;
        var count = selectedFiles.length;

        for(var index in selectedFiles) {
            if(selectedFiles[index].uploaded === false) {
                allFilesUploaded = false;
                await uploadFile(selectedFiles[index], index);
                if(index != count - 1) {
                    await sleep(1000); //1 seconds
                }
                else {
                    await sleep(500); //1/2 seconds
                }
            }
        }

        for(var index in selectedFiles) {
            if(selectedFiles[index].uploaded === false) {
                closeDialog = false;
            }
        }

        if(closeDialog) {
            hideModalDialog();
            setSelectedFiles([]);

            if(props.isProduct) {
                dispatch(commonActions.setSuccessMessage("Product Document(s) Added Successfully!"))
            }
            else {
                dispatch(commonActions.setSuccessMessage("Document(s) Added Successfully!"))
            }
        }
        else if(allFilesUploaded) {
            dispatch(commonActions.setErrorMessage("All selected files are uploaded."));
        }
    }

    const onDeleteClick = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    }

    const onDeleteConfirm = async () => {
        setShowDeleteModal(false);

        if(deleteId > 0) {
            setLoading(true);
        
            var data = await documentActions.deleteServiceDocument(deleteId, props.serviceId, props.refId, props.refName, props.parentId, props.parentName, props.isProduct, (props.skipAudit || false));
            if(data.error) {
                dispatch(commonActions.setErrorMessage(data.error));
            }
            else {
                setFBOState(data.fbo);
                setDocumentList(data.list);

                if(props.onChange) {
                    props.onChange(props.isProduct);
                }

                if(props.isProduct) {
                    dispatch(commonActions.setSuccessMessage("Product Document Delete Successfully!"))
                }
                else {
                    dispatch(commonActions.setSuccessMessage("Document Delete Successfully!"))
                }
            }
    
            setLoading(false);
        }
    }

    const onDownloadServiceTemplate = (templateName) => {
        dispatch(documentActions.downloadServiceTemplate(templateName));
    };

    const onDownloadServiceDocument = (id, documentName) => {
        dispatch(documentActions.downloadServiceDocument(id, documentName));
    };

    const showPhotoModalDialog = (item) => {
        setSelectedItem(item);
        setShowPhotoModal(true);
    }

    const hidePhotoModalDialog = () => {
        setShowPhotoModal(false);
    }

    const showPdfModalDialog = (item) => {
        setSelectedItem(item);
        setShowPdfModal(true);
    }

    const hidePdfModalDialog = () => {
        setShowPdfModal(false);
    }

    const showWordModalDialog = (item) => {
        setSelectedItem(item);
        setShowWordModal(true);
    }

    const hideWordModalDialog = () => {
        setShowWordModal(false);
    }

    const isPhotoFile = (fileName) => {
        var photoList = ["jpeg", "jpg", "bmp", "gif", "png", "tiff", "webp", "eps"];
        var extension = getFileExtension(fileName);

        if(photoList.indexOf(extension.toLowerCase()) !== -1) {
            return true;
        }

        return false;
    }

    const isPdfFile = (fileName) => {
        var pdfList = ["pdf"];
        var extension = getFileExtension(fileName);

        if(pdfList.indexOf(extension.toLowerCase()) !== -1) {
            return true;
        }

        return false;
    }

    const isWordFile = (fileName) => {
        var wordList = ["doc", "docx", "xls", "xlsx"];
        var extension = getFileExtension(fileName);

        if(wordList.indexOf(extension.toLowerCase()) !== -1) {
            return true;
        }

        return false;
    }

    const getItemCardClass  = (item) => {
        if(item.code === "CDR_STRUCTURAL") {
            if(props.showStructuralDocument === false) {
                return "d-none";
            }
        }
        if(item.code === "CDR_STRUCTURAL_MODEL") {
            if(props.showStructuralModelDocument === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_ARCHITECTURAL") {
            if(props.showArchitecturalDocument === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_SOIL") {
            if(props.showSoilDocument === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_AUTO_CAD") {
            if(props.showAutoCadDocument === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_CALCULATION") {
            if(props.showCalculationDocument === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_WATER_PROOF") {
            if(props.showWaterproofingDocument === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_RD0") {
            if(props.showRd0Document === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_DRR") {
            if(props.showDrrDocument === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_RD3") {
            if(props.showRd3Document === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_RD6") {
            if(props.showRd6Document === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_BALADY") {
            if(props.showBaladyDocument === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_RD5") {
            if(props.showRd5Document === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_INR") {
            if(props.showInspectionDocument === false) {
                return "d-none";
            }
        }
        else if(item.code === "CDR_RD7") {
            if(props.showRd7Document === false) {
                return "d-none";
            }
        }
                
        return "card-light";
    };

    const canEditItem  = (item) => {
        if(item.code === "CDR_STRUCTURAL" && props.canEditStructuralDocument === true) {
            return true;
        }
        if(item.code === "CDR_STRUCTURAL_MODEL" && props.canEditStructuralModelDocument === true) {
            return true;
        }
        else if(item.code === "CDR_ARCHITECTURAL" && props.canEditArchitecturalDocument === true) {
            return true;
        }
        else if(item.code === "CDR_SOIL" && props.canEditSoilDocument === true) {
            return true;
        }
        else if(item.code === "CDR_AUTO_CAD" && props.canEditAutoCadDocument === true) {
            return true;
        }
        else if(item.code === "CDR_CALCULATION" && props.canEditCalculationDocument === true) {
            return true;
        }
        else if(item.code === "CDR_WATER_PROOF" && props.canEditWaterproofingDocument === true) {
            return true;
        }
        else if(item.code === "CDR_RD0" && props.canEditRd0Document === true) {
            return true;
        }
        else if(item.code === "CDR_DRR" && props.canEditDrrDocument === true) {
            return true;
        }
        else if(item.code === "CDR_RD3" && props.canEditRd3Document === true) {
            return true;
        }
        else if(item.code === "CDR_RD6" && props.canEditRd6Document === true) {
            return true;
        }
        else if(item.code === "CDR_BALADY" && props.canEditBaladyDocument === true) {
            return true;
        }
        else if(item.code === "CDR_RD5" && props.canEditRd5Document === true) {
            return true;
        }
        else if(item.code === "CDR_INR" && props.canEditInspectionDocument === true) {
            return true;
        }
        else if(item.code === "CDR_RD7" && props.canEditRd7Document === true) {
            return true;
        }
        else if(item.code === "OT" && props.canChange === true) {
            return true;
        }

        return false;
    };

    const isItemRequired  = (item) => {
        if(item.code === "CDR_STRUCTURAL" && props.isStructuralDocumentRequired === true) {
            return true;
        }
        if(item.code === "CDR_STRUCTURAL_MODEL" && props.isStructuralModelDocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_ARCHITECTURAL" && props.isArchitecturalDocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_SOIL" && props.isSoilDocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_AUTO_CAD" && props.isAutoCadDocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_CALCULATION" && props.isCalculationDocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_WATER_PROOF" && props.isWaterproofingDocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_RD0" && props.isRd0DocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_DRR" && props.isDrrDocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_RD3" && props.isRd3DocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_RD6" && props.isRd6DocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_BALADY" && props.isBaladyDocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_RD5" && props.isRd5DocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_INR" && props.isInspectionDocumentRequired === true) {
            return true;
        }
        else if(item.code === "CDR_RD7" && props.isRd7DocumentRequired === true) {
            return true;
        }
                        
        return false;
    };

    return (
        <Fragment>
            <Card className={`${(props.refName === "IDI_PROJECT_STATE") ? "mb-0 card-light mt-3" : "mb-3"}`}>
                <CardHeader className={`${props.refName === "IDI_PROJECT_STATE" ? "d-none" : "border-0 d-flex align-items-center"}`}>
                    <CardLoader loading={loading || downloading} />
                    <h5 className="card-title mb-0 flex-grow-1 cursor-pointer" onClick={() => setGridCollapsed(!gridCollapsed)}>
                        {gridCollapsed && <i className="ri-add-box-line align-bottom text-primary"></i>}
                        {!gridCollapsed && <i className="ri-checkbox-indeterminate-line align-bottom text-primary"></i>}
                        {props.isProduct ? "Product " : ""}Attachments / Documents ({gridCollapsed ? "Expand" : "Collapse"})
                    </h5>
                </CardHeader>
                <CardBody className="border border-dashed border-end-0 border-start-0">
                    <Collapse isOpen={!gridCollapsed} className="collapse">
                        {documentList.map(function(item, index){
                            return(
                                <Card className={getItemCardClass(item)} key={index}>
                                    <CardHeader className="d-flex">
                                        <div className="flex-grow-1">
                                            <h4 className="card-title mb-2">{item.name}{isItemRequired(item) && <span className="required">*</span>}</h4>
                                            <h6 className="card-subtitle font-14 text-muted">{item.instruction}</h6>
                                        </div>
                                        {item.templateName &&
                                            <button type="button" className="btn btn-secondary btn-icon float-end rounded-pill ms-1" title="Download Template"
                                                onClick={() => onDownloadServiceTemplate(item.templateName)}
                                            >
                                                <i className="ri-download-line" />
                                            </button>
                                        }
                                        {canEditItem(item) &&
                                        <button type="button" className="btn btn-primary btn-icon float-end rounded-pill ms-1" title="Upload Document"
                                            onClick={() => showModalDialog(item)}
                                        >
                                            <i className="ri-upload-line" />
                                        </button>
                                        }
                                    </CardHeader>
                                    <CardBody className="card-text">
                                        {item.parentDocumentList.length > 0 &&
                                            <Fragment>
                                                <Row className="gy-2 pt-1"><h6>Container Documents</h6></Row>
                                                {item.parentDocumentList.map(function(document, index){
                                                    return(
                                                        <Row className="gy-2 pt-1 font-14" key={index}>
                                                            <Col xxl={3} md={6}>{document.title}</Col>
                                                            <Col xxl={3} md={6}>{document.name}</Col>
                                                            <Col xxl={3} md={6}><MyDateRenderer value={document.dateAdded} /></Col>
                                                            <Col xxl={3} md={6}>
                                                                <a href={`${buildDownloadUrl('Document/DownloadDocument', { id: document.id })}`} target="_blank" className="btn btn-primary btn-sm me-1" title="Download Document">
                                                                    <i className="ri-download-line" /> Download
                                                                </a>
                                                                {/* <button type="button" className="btn btn-primary btn-sm me-1" title="Download Document"
                                                                    onClick={() => onDownloadServiceDocument(document.id, document.name)}
                                                                >
                                                                    <i className="ri-download-line" /> Download
                                                                </button> */}
                                                            </Col>
                                                        </Row>
                                                    );
                                                })}
                                                <Row className="gy-2 pt-3"><hr /></Row>
                                            </Fragment>
                                        }
                                        {item.documentList.length == 0 && <div className="font-14">No Document Uploaded</div>}
                                        {item.documentList.map(function(document, index){
                                            return(
                                                <Row className="gy-2 pt-1 font-14" key={index}>
                                                    <Col xxl={3} md={6}>{document.title}</Col>
                                                    <Col xxl={3} md={6}>{document.name}</Col>
                                                    <Col xxl={3} md={6}><MyDateRenderer value={document.dateAdded} /></Col>
                                                    <Col xxl={3} md={6}>
                                                        <a href={`${buildDownloadUrl('Document/DownloadDocument', { id: document.id })}`} target="_blank" className="btn btn-primary btn-sm me-1" title="Download Document">
                                                            <i className="ri-download-line" /> Download
                                                        </a>
                                                        {/* <button type="button" className="btn btn-primary btn-sm me-1" title="Download Document"
                                                            onClick={() => onDownloadServiceDocument(document.id, document.name)}
                                                        >
                                                            <i className="ri-download-line" /> Download
                                                        </button> */}
                                                        {isPhotoFile(document.name) && 
                                                            <button type="button" className="btn btn-primary btn-sm me-1" title="View Document"
                                                                onClick={() => showPhotoModalDialog(document)}
                                                            >
                                                                <i className="ri-fullscreen-line" /> View
                                                            </button>
                                                        }
                                                        {isPdfFile(document.name) && 
                                                            <button type="button" className="btn btn-primary btn-sm me-1" title="View Document"
                                                                onClick={() => showPdfModalDialog(document)}
                                                            >
                                                                <i className="ri-fullscreen-line" /> View
                                                            </button>
                                                        }
                                                        {isWordFile(document.name) && 
                                                            <button type="button" className="btn btn-primary btn-sm me-1" title="View Document"
                                                                onClick={() => showWordModalDialog(document)}
                                                            >
                                                                <i className="ri-fullscreen-line" /> View
                                                            </button>
                                                        }
                                                        {canEditItem(item) &&
                                                        <button type="button" className="btn btn-danger btn-sm me-1" title="Delete Document"
                                                            onClick={() => onDeleteClick(document.id)}
                                                        >
                                                            <i className="ri-delete-bin-2-line" /> Delete
                                                        </button>
                                                        }
                                                    </Col>
                                                </Row>
                                            );
                                        })}
                                    </CardBody>
                                </Card>
                            );
                        })}
                    </Collapse>
                </CardBody>
            </Card>

            <Modal tabIndex="-1" size="xl" isOpen={showModal} toggle={hideModalDialog} centered={true}>
                <ModalHeader>
                    Upload Documents
                </ModalHeader>
                <ModalBody>
                    <CardLoader loading={saving} />
                    <Row className="gy-2">
                        <Col xxl={12} md={12}>
                            <label className="form-label">Document Type</label>
                            <select className="form-select" disabled={true}
                                value={(fboState && fboState.documentType) || ""}
                                onChange={e => onDocumentChange("documentType", e.target.value)}
                            >
                                <option value={""}>...</option>
                                {documentTypeList && documentTypeList.map(function(item, index){
                                    return (
                                        <option key={index} value={item.code}>{item.description}</option>
                                    );
                                })}
                            </select>
                        </Col>
                        <Col xxl={12} md={12}>
                            <label className="btn btn-primary btn-label mb-0 float-end">
                                <i className="ri-check-double-line label-icon align-bottom"></i> Choose File(s)
                                <input className="d-none" type="file" onChange={onFileChange} multiple={allowMultiple} accept={defaultFileType} />
                            </label>
                        </Col>
                        <Col xxl={12} md={12}>
                            <Table className="table-hover table-bordered align-middle mb-0">
                                <thead className="table-primary">
                                    <tr>
                                        <th style={{width:"40%"}}>Title</th>
                                        <th style={{width:"45%"}}>File Name</th>
                                        <th style={{width:"5%"}}>&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedFiles.length === 0 && <tr><td colSpan={3}>No file(s) Selected</td></tr>}
                                    {selectedFiles.map(function(item, index){
                                        return(
                                            <tr key={index}>
                                                <td>
                                                    <div className="col-sm-12">
                                                        <input className={`form-control ${item.titleValid ? "" : "is-invalid"}`} readOnly={item.uploaded}
                                                            placeholder={"Add Title..."}
                                                            value={item.title}
                                                            onChange={e => onFileTitleChange(index, e.target.value)}
                                                        />
                                                    </div>
                                                </td>
                                                <td style={{verticalAlign:"middle"}}>
                                                    <div className="col-sm-12">
                                                        <strong>{item.name}</strong>
                                                    </div>
                                                    {item.error && <Progress color="danger" style={{height:"25px"}} value={100}> {item.error} </Progress>}
                                                    {!item.error && <Progress color="primary" style={{height:"25px"}} value={item.uploaded ? 100 : item.progress}> {item.uploaded ? 100 : item.progress}% </Progress>}
                                                </td>
                                                <td className="text-center">
                                                    {!item.uploaded &&
                                                    <button type="button" className="btn btn-danger btn-icon rounded-pill ms-1" title="Remove Photo"
                                                        onClick={() => onFileDelete(index)}
                                                    >
                                                        <i className="ri-delete-bin-2-line"></i>
                                                    </button>
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn btn-primary btn-label" onClick={onSaveDocumentClick} disabled={loading || saving}>
                        <i className="ri-save-line label-icon align-bottom"></i> Save Document(s)
                    </button>
                    <button type="button" className="btn btn-danger btn-label" data-dismiss="modal" onClick={hideModalDialog} disabled={loading || saving}>
                        <i className="ri-close-line label-icon align-bottom"></i> Close
                    </button>
                </ModalFooter>
            </Modal>

            <PhotoModal item={selectedItem} show={showPhotoModal} onCloseClick={hidePhotoModalDialog} />
            <PdfModal item={selectedItem} show={showPdfModal} onCloseClick={hidePdfModalDialog} />
            <WordModal item={selectedItem} show={showWordModal} onCloseClick={hideWordModalDialog} />

            <ConfirmModal message={"Are you sure you want to delete the Document?"} show={showDeleteModal} onCloseClick={() => setShowDeleteModal(false)} onYesClick={onDeleteConfirm} />
            <ErrorModal message={`A document is already uploaded in \"${multipleFileMessage || "this"}\" section for this ticket. Only one document is allowed in \"${multipleFileMessage || "this"}\" section.`} show={showMultipleFileMessageModal} onCloseClick={() => setShowMultipleFileMessageModal(false)} />
        </Fragment>
    );
};

export default IdiServiceDocumentGrid;